import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FirebaseDB } from '../../firebase/config';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

/**
 * ContactReminderModal
 *
 * Props:
 *   - cliente: object  { id, nombre, email, telefono, frecuenciaPedidos, fechaUltimoPedido }
 *   - onClose: fn()
 *
 * Canal Email   → llama a la Cloud Function sendReminder (Brevo) con soporte para adjunto.
 * Canal WhatsApp → abre un enlace wa.me nativo en una nueva pestaña (los adjuntos no son soportados por wa.me).
 */

const buildDefaultMessage = (cliente) => {
    const frec = cliente.frecuenciaPedidos ? `cada ${cliente.frecuenciaPedidos}` : 'regularmente';
    return `Hola ${cliente.nombre || 'estimado cliente'},

Le escribimos del equipo de Globaclean para recordarle que ya se acerca el momento de su próximo pedido. 

Sabemos que usted suele surtirse ${frec}, y queremos asegurarnos de que no le falten los productos que necesita.

No dude en contactarnos para hacer su pedido o si tiene alguna consulta.

Saludos,
Equipo Globaclean`;
};

const cleanPhone = (phone) => phone.replace(/[\s\-().+]/g, '');

export const ContactReminderModal = ({ cliente, onClose }) => {
    const [channel, setChannel] = useState('email');
    const [message, setMessage] = useState(() => buildDefaultMessage(cliente));
    const [sending, setSending] = useState(false);
    
    // Estados para el archivo adjunto
    const [attachedFile, setAttachedFile] = useState(null);
    const [fileBase64, setFileBase64] = useState('');
    const [fileName, setFileName] = useState('');

    const hasEmail = !!cliente.email;
    const hasPhone = !!cliente.telefono;

    /* ── Guardar registro de contacto en Firestore ──────────── */
    const saveContactLog = async (canal) => {
        try {
            const clienteRef = doc(FirebaseDB, 'clientes', cliente.id);
            await updateDoc(clienteRef, {
                ultimoContacto: {
                    canal,
                    fecha: serverTimestamp(),
                },
            });
        } catch (err) {
            console.warn('No se pudo guardar el registro de contacto:', err);
        }
    };

    /* ── Conversión de archivo a Base64 ─────────────────────── */
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setAttachedFile(null);
            setFileBase64('');
            setFileName('');
            return;
        }

        // Limitar tamaño a 5MB para Cloud Functions
        if (file.size > 5 * 1024 * 1024) {
            Swal.fire({
                icon: 'error',
                title: 'Archivo muy grande',
                text: 'El tamaño máximo permitido para adjuntos es de 5MB.',
            });
            e.target.value = ''; // Limpiar el input
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            // El resultado viene con el prefijo "data:application/pdf;base64,"
            // Brevo necesita únicamente la parte codificada en base64 pura.
            const base64Content = reader.result.split(',')[1];
            setAttachedFile(file);
            setFileBase64(base64Content);
            setFileName(file.name);
        };
        reader.onerror = (error) => {
            console.error('Error al leer el archivo:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error de lectura',
                text: 'No se pudo procesar el archivo seleccionado.',
            });
        };
        reader.readAsDataURL(file);
    };

    /* ── Envío por WhatsApp (wa.me nativo) ────────────────── */
    const handleSendWhatsApp = async () => {
        if (!message.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Mensaje vacío',
                text: 'Por favor escribe un mensaje antes de continuar.',
                toast: true,
                position: 'top-end',
                timer: 3000,
                showConfirmButton: false,
            });
            return;
        }

        if (!hasPhone) {
            Swal.fire({
                icon: 'error',
                title: 'Sin teléfono',
                text: 'Este cliente no tiene teléfono registrado.',
                toast: true,
                position: 'top-end',
                timer: 3500,
                showConfirmButton: false,
            });
            return;
        }

        const phone = cleanPhone(cliente.telefono);
        const encodedMsg = encodeURIComponent(message);
        const url = `https://wa.me/${phone}?text=${encodedMsg}`;

        window.open(url, '_blank', 'noopener,noreferrer');

        // Guardar registro de contacto
        await saveContactLog('whatsapp');

        Swal.fire({
            icon: 'success',
            title: '¡WhatsApp abierto!',
            text: `Se abrió WhatsApp para ${cliente.nombre}. Solo confirma el envío desde la app.`,
            toast: true,
            position: 'top-end',
            timer: 4500,
            showConfirmButton: false,
        });

        onClose();
    };

    /* ── Envío por Email (Cloud Function / Brevo) ──────────── */
    const handleSendEmail = async () => {
        if (!message.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Mensaje vacío',
                text: 'Por favor escribe un mensaje antes de enviar.',
                toast: true,
                position: 'top-end',
                timer: 3000,
                showConfirmButton: false,
            });
            return;
        }

        if (!hasEmail) {
            Swal.fire({
                icon: 'error',
                title: 'Sin email',
                text: 'Este cliente no tiene email registrado.',
                toast: true,
                position: 'top-end',
                timer: 3500,
                showConfirmButton: false,
            });
            return;
        }

        setSending(true);
        try {
            const functions = getFunctions();
            const sendReminder = httpsCallable(functions, 'sendReminder');
            
            // Construimos la petición con los parámetros del adjunto si existe
            const payload = { 
                clienteId: cliente.id, 
                channel: 'email', 
                message 
            };

            if (fileBase64 && fileName) {
                payload.attachmentBase64 = fileBase64;
                payload.attachmentName = fileName;
            }

            await sendReminder(payload);

            // Guardar registro de contacto
            await saveContactLog('email');

            Swal.fire({
                icon: 'success',
                title: '¡Enviado!',
                text: `Correo enviado a ${cliente.email}`,
                toast: true,
                position: 'top-end',
                timer: 4000,
                showConfirmButton: false,
            });
            onClose();
        } catch (err) {
            console.error('sendReminder error:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error al enviar',
                text: err?.message || 'Hubo un problema al enviar el correo. Intenta de nuevo.',
                toast: true,
                position: 'top-end',
                timer: 5000,
                showConfirmButton: false,
            });
        } finally {
            setSending(false);
        }
    };

    /* ── Dispatcher principal ──────────────────────────────── */
    const handleSend = () => {
        if (channel === 'whatsapp') {
            handleSendWhatsApp();
        } else {
            handleSendEmail();
        }
    };

    return (
        /* ── Backdrop ───────────────────────────────────────── */
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                backgroundColor: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'fadeIn 0.2s ease',
            }}
        >
            <style>{`
                @keyframes fadeIn  { from { opacity:0; transform:scale(.95); } to { opacity:1; transform:scale(1); } }
                .crm-radio-card { border:2px solid #e5e7eb; border-radius:10px; padding:12px 18px; cursor:pointer; transition:all .18s; display:flex; align-items:center; gap:10px; flex:1; }
                .crm-radio-card:hover { border-color:#6366f1; background:#f5f3ff; }
                .crm-radio-card.active { border-color:#6366f1; background:#ede9fe; }
                .crm-radio-card input { accent-color:#6366f1; width:16px; height:16px; }
                .crm-textarea { width:100%; min-height:160px; resize:vertical; border:1.5px solid #d1d5db; border-radius:8px; padding:12px; font-size:14px; font-family:inherit; line-height:1.6; outline:none; transition:border-color .18s; box-sizing:border-box; }
                .crm-textarea:focus { border-color:#6366f1; box-shadow:0 0 0 3px rgba(99,102,241,.12); }
                .crm-btn-send { background:linear-gradient(135deg,#6366f1,#8b5cf6); color:#fff; border:none; border-radius:8px; padding:10px 24px; font-size:15px; font-weight:600; cursor:pointer; transition:transform .15s,box-shadow .15s; }
                .crm-btn-send:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 6px 20px rgba(99,102,241,.35); }
                .crm-btn-send:disabled { opacity:.55; cursor:not-allowed; }
                .crm-btn-cancel { background:#f3f4f6; color:#374151; border:none; border-radius:8px; padding:10px 20px; font-size:15px; font-weight:600; cursor:pointer; transition:background .15s; }
                .crm-btn-cancel:hover { background:#e5e7eb; }
                .crm-wa-note { font-size:12px; color:#059669; background:#ecfdf5; border:1px solid #a7f3d0; border-radius:6px; padding:8px 12px; margin-top:8px; }
                
                /* File Input custom styling */
                .crm-file-upload { border:1.5px dashed #d1d5db; border-radius:8px; padding:12px; text-align:center; cursor:pointer; transition:border-color .18s, background .18s; position:relative; background:#f9fafb; margin-top:10px; }
                .crm-file-upload:hover { border-color:#6366f1; background:#f5f3ff; }
                .crm-file-input { position:absolute; top:0; left:0; width:100%; height:100%; opacity:0; cursor:pointer; }
            `}</style>

            {/* ── Card ───────────────────────────────────────── */}
            <div style={{
                background: '#fff',
                borderRadius: '16px',
                boxShadow: '0 25px 60px rgba(0,0,0,.2)',
                width: '100%',
                maxWidth: '540px',
                padding: '32px',
                animation: 'fadeIn 0.22s ease',
            }}>
                {/* Header */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'22px' }}>
                    <div>
                        <h2 style={{ margin:0, fontSize:'20px', fontWeight:'700', color:'#111827' }}>
                            📩 Enviar Recordatorio
                        </h2>
                        <p style={{ margin:'4px 0 0', fontSize:'14px', color:'#6b7280' }}>
                            Cliente: <strong>{cliente.nombre}</strong>
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background:'none', border:'none', fontSize:'22px', cursor:'pointer', color:'#9ca3af', lineHeight:1 }}
                        title="Cerrar"
                    >
                        ×
                    </button>
                </div>

                {/* Channel selector */}
                <p style={{ fontSize:'13px', fontWeight:'600', color:'#6b7280', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'.5px' }}>
                    Canal de envío
                </p>
                <div style={{ display:'flex', gap:'12px', marginBottom:'16px' }}>
                    {/* Email */}
                    <label className={`crm-radio-card ${channel === 'email' ? 'active' : ''}`}>
                        <input
                            type="radio"
                            name="channel"
                            value="email"
                            checked={channel === 'email'}
                            onChange={() => setChannel('email')}
                        />
                        <div>
                            <div style={{ fontWeight:'600', fontSize:'14px', color:'#111827' }}>📧 Email</div>
                            <div style={{ fontSize:'12px', color: hasEmail ? '#6b7280' : '#ef4444' }}>
                                {hasEmail ? cliente.email : 'Sin email registrado'}
                            </div>
                        </div>
                    </label>

                    {/* WhatsApp */}
                    <label className={`crm-radio-card ${channel === 'whatsapp' ? 'active' : ''}`}>
                        <input
                            type="radio"
                            name="channel"
                            value="whatsapp"
                            checked={channel === 'whatsapp'}
                            onChange={() => setChannel('whatsapp')}
                        />
                        <div>
                            <div style={{ fontWeight:'600', fontSize:'14px', color:'#111827' }}>💬 WhatsApp</div>
                            <div style={{ fontSize:'12px', color: hasPhone ? '#6b7280' : '#ef4444' }}>
                                {hasPhone ? cliente.telefono : 'Sin teléfono registrado'}
                            </div>
                        </div>
                    </label>
                </div>

                {/* Nota informativa cuando se selecciona WhatsApp */}
                {channel === 'whatsapp' && (
                    <p className="crm-wa-note">
                        💡 Se abrirá WhatsApp en una nueva pestaña con el mensaje prellenado. Solo deberás confirmar el envío desde la app.
                    </p>
                )}

                {/* Message textarea */}
                <p style={{ fontSize:'13px', fontWeight:'600', color:'#6b7280', marginBottom:'8px', marginTop:'18px', textTransform:'uppercase', letterSpacing:'.5px' }}>
                    Mensaje
                </p>
                <textarea
                    className="crm-textarea"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Escribe el mensaje de recordatorio..."
                />
                <p style={{ fontSize:'12px', color:'#9ca3af', marginTop:'6px' }}>
                    Puedes editar el mensaje antes de enviarlo.
                </p>

                {/* ── Carga de archivos adjuntos (Solo para Email) ───── */}
                {channel === 'email' && (
                    <div style={{ marginTop: '16px' }}>
                        <p style={{ fontSize:'13px', fontWeight:'600', color:'#6b7280', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'.5px' }}>
                            Adjuntar Archivo (PDF, XML, Imagen, etc. Máx 5MB)
                        </p>
                        <div className="crm-file-upload">
                            <input 
                                type="file" 
                                className="crm-file-input" 
                                onChange={handleFileChange}
                            />
                            <span style={{ fontSize: '13px', color: '#4b5563' }}>
                                {attachedFile ? `📎 ${attachedFile.name} (${(attachedFile.size / 1024).toFixed(1)} KB)` : '📁 Haz clic aquí o arrastra un archivo'}
                            </span>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end', marginTop:'24px' }}>
                    <button className="crm-btn-cancel" onClick={onClose} disabled={sending}>
                        Cancelar
                    </button>
                    <button className="crm-btn-send" onClick={handleSend} disabled={sending}>
                        {sending
                            ? '⏳ Enviando...'
                            : channel === 'whatsapp'
                                ? '💬 Abrir en WhatsApp'
                                : '📧 Enviar por Email'}
                    </button>
                </div>
            </div>
        </div>
    );
};
